import { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import safeJsonParse from '@/utils/safeJsonParse';
import { getUserScopedKey } from '@/utils';
import { RHI_SESSION_KEYS, RHI_TTL_VALUES } from '@/constants/queryKeys';

const RHI_FORM_SNAPSHOT_KEYS = [
  RHI_SESSION_KEYS.SELECTED_MEMBERS_FORM,
  RHI_SESSION_KEYS.PROPOSER_FORM,
  RHI_SESSION_KEYS.NOMINEE_FORM,
  RHI_SESSION_KEYS.BANK_DETAILS_FORM,
  RHI_SESSION_KEYS.PROPOSAL_FORM,
];

/**
 * Drops every draft this hook has written. Called when the user lands back on
 * the RHI home page: a snapshot restored there is a silent prefill they never
 * asked for, and it wins over the member details the backend sends. Resuming
 * a real journey is a deliberate choice made from the home page instead.
 */
export const clearRhiFormSnapshots = () => {
  RHI_FORM_SNAPSHOT_KEYS.forEach((key) =>
    window.sessionStorage.removeItem(getUserScopedKey(key)),
  );
};

export const useFormPersist = <T extends Record<string, any>>(
  storageKey: string,
  form: UseFormReturn<T>,
  ttl: number = RHI_TTL_VALUES.HEALTH_INSURANCE_FORMS,
  options?: {
    excludeFields?: Array<keyof T>;
  },
) => {
  const scopedKey = getUserScopedKey(storageKey);
  const excludeFields = options?.excludeFields ?? [];

  const getPersistableData = (data: T) => {
    if (!excludeFields.length) return data;

    const nextData = { ...data };
    excludeFields.forEach((fieldName) => {
      delete nextData[fieldName];
    });
    return nextData as T;
  };

  useEffect(() => {
    const stored = sessionStorage.getItem(scopedKey);
    if (!stored) return;

    const parsed = safeJsonParse<{ data: T; expiresAt?: number }>(stored);
    if (!parsed) return;

    const { data, expiresAt } = parsed;

    if (ttl && expiresAt && expiresAt < Date.now()) {
      sessionStorage.removeItem(scopedKey);
      return;
    }

    form.reset(getPersistableData(data));
  }, [scopedKey, form, ttl]);

  useEffect(() => {
    const subscription = form.watch((data) => {
      try {
        const persistableData = getPersistableData(data as T);
        const snapshot = { data: persistableData, expiresAt: Date.now() + ttl };
        sessionStorage.setItem(scopedKey, JSON.stringify(snapshot));
      } catch (error) {
        window.dev.e('Failed to persist form data', error);
      }
    });

    return () => subscription.unsubscribe();
  }, [form, scopedKey, ttl]);
};
